import { useState, useRef, useCallback, useMemo, useEffect } from "react";
import {
  Upload, Download, FileSpreadsheet, CheckCircle, XCircle,
  AlertTriangle, AlertCircle, FileDown,
} from "lucide-react";
import { assetApi } from "../api";
import type { ImportResultRow } from "../types";
import { BackBtn, Btn, Card, CardHeader, PageHeader } from "../components/AssetUI";

const PAGE_SIZE = 10;

export default function AssetImportPage() {
  const [file, setFile]         = useState<File | null>(null);
  const [loading, setLoading]   = useState(false);
  const [results, setResults]   = useState<ImportResultRow[] | null>(null);
  const [summary, setSummary]   = useState<{ total: number; success: number; failed: number } | null>(null);
  const [error, setError]       = useState<string | null>(null);
  const [dlLoading, setDlLoading]     = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [resultsPage, setResultsPage] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);

  async function downloadTemplate() {
    setDlLoading(true);
    try {
      const blob = await assetApi.downloadTemplate();
      triggerDownload(blob, "asset_import_template.xlsx");
    } catch { /* silent */ }
    setDlLoading(false);
  }

  async function doImport() {
    if (!file) return;
    setLoading(true); setResults(null); setSummary(null); setError(null); setResultsPage(0);
    try {
      const res = await assetApi.importAssets(file);
      setResults(res.results ?? []);
      setSummary({ total: res.total, success: res.success, failed: res.failed });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Import failed");
    }
    setLoading(false);
  }

  const downloadErrorReport = useCallback(async () => {
    const failed = (results ?? []).filter((r) => !r.success);
    if (!failed.length) return;
    setReportLoading(true);
    try {
      const blob = await assetApi.downloadErrorReport(failed);
      triggerDownload(blob, `import-errors-${new Date().toISOString().slice(0,10)}.xlsx`);
    } catch { /* silent */ }
    setReportLoading(false);
  }, [results]);

  const failedCount = (results ?? []).filter((r) => !r.success).length;
  const warnCount    = (results ?? []).filter((r) => r.success && (r.warnings?.length ?? 0) > 0).length;
  const successCount = (results ?? []).filter((r) => r.success && !(r.warnings?.length)).length;

  const totalPages = Math.max(1, Math.ceil((results?.length ?? 0) / PAGE_SIZE));
  const pagedResults = useMemo(
    () => (results ?? []).slice(resultsPage * PAGE_SIZE, resultsPage * PAGE_SIZE + PAGE_SIZE),
    [results, resultsPage],
  );

  useEffect(() => { setResultsPage(0); }, [results]);

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumb="Asset Management"
        title="Import Assets"
        actions={<BackBtn to="/assets" label="Dashboard" />}
      />

      {/* Template download */}
      <Card>
        <CardHeader title="Download Template" />
        <div className="flex items-center gap-4 p-5">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-50">
            <FileSpreadsheet className="h-6 w-6 text-emerald-600" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-slate-800">asset_import_template.xlsx</p>
          </div>
          <Btn variant="emerald" icon={Download} loading={dlLoading} loadingText="Downloading…" onClick={downloadTemplate}>
            Download
          </Btn>
        </div>
      </Card>

      {/* Upload */}
      <Card>
        <CardHeader title="Upload File" subtitle="Accepts .xlsx files only. Max 5 MB." />
        <div className="space-y-4 p-5">
          <div
            className={`relative flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed p-10 transition
              ${file ? "border-indigo-400 bg-indigo-50/40" : "border-slate-200 bg-slate-50 hover:border-indigo-300"}`}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) setFile(f); }}
          >
            <Upload className="h-8 w-8 text-slate-300" />
            {file ? (
              <div className="text-center">
                <p className="font-semibold text-indigo-700">{file.name}</p>
                <p className="text-xs text-slate-400">{(file.size / 1024).toFixed(1)} KB</p>
                <button type="button" className="mt-2 text-xs text-red-500 hover:underline" onClick={() => { setFile(null); setResults(null); setSummary(null); }}>
                  Remove
                </button>
              </div>
            ) : (
              <div className="text-center">
                <p className="text-sm font-semibold text-slate-700">
                  Drop your file here or{" "}
                  <button type="button" className="text-indigo-600 underline" onClick={() => fileRef.current?.click()}>
                    browse
                  </button>
                </p>
                <p className="mt-1 text-xs text-slate-400">Supported: .xlsx</p>
              </div>
            )}
            <input
              ref={fileRef} type="file" accept=".xlsx" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) setFile(f); }}
            />
          </div>
          <div className="flex justify-end">
            <Btn variant="primary" icon={Upload} loading={loading} loadingText="Importing…" disabled={!file} onClick={doImport}>
              Import Assets
            </Btn>
          </div>
          {error && (
            <div className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
              <AlertCircle className="h-5 w-5 shrink-0 text-red-500" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
        </div>
      </Card>

      {/* Results */}
      {results && summary && (
        <Card>
          <CardHeader
            title="Import Complete"
            subtitle={`${summary.total} rows — ${summary.success} imported, ${summary.failed} failed${warnCount ? `, ${warnCount} with warnings` : ""}`}
            action={
              failedCount > 0 && (
                <Btn
                  variant="secondary"
                  size="sm"
                  icon={FileDown}
                  loading={reportLoading}
                  loadingText="Generating…"
                  onClick={downloadErrorReport}
                >
                  Download Error Report
                </Btn>
              )
            }
          />

          <div className="overflow-x-auto border-t">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-3">Row</th>
                  <th className="px-4 py-3">Asset Number</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {pagedResults.map((r) => (
                  <tr key={r.row} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono text-slate-600">{r.row}</td>
                    <td className="px-4 py-3 font-semibold text-slate-800">{r.assetNumber || "—"}</td>
                    <td className="px-4 py-3">
                      {!r.success ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
                          <XCircle className="h-3.5 w-3.5" /> Failed
                        </span>
                      ) : (r.warnings?.length ?? 0) > 0 ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
                          <AlertTriangle className="h-3.5 w-3.5" /> Warning
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                          <CheckCircle className="h-3.5 w-3.5" /> Imported
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {!r.success
                        ? (r.error ?? "Unknown error")
                        : (r.warnings ?? []).join("; ") || "—"}
                    </td>
                  </tr>
                ))}
                {pagedResults.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400">No rows to show.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between border-t px-5 py-3 text-sm">
            <span className="flex gap-4 text-xs text-slate-500">
              <span className="text-emerald-600">{successCount} imported</span>
              <span className="text-amber-600">{warnCount} warnings</span>
              <span className="text-red-600">{failedCount} failed</span>
            </span>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Page {resultsPage + 1} of {totalPages}</span>
              <button onClick={() => setResultsPage((p) => Math.max(0, p - 1))} disabled={resultsPage === 0}
                className="rounded-lg border px-3 py-1.5 disabled:opacity-40 hover:bg-slate-50">Prev</button>
              <button onClick={() => setResultsPage((p) => Math.min(totalPages - 1, p + 1))} disabled={resultsPage >= totalPages - 1}
                className="rounded-lg border px-3 py-1.5 disabled:opacity-40 hover:bg-slate-50">Next</button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a   = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}
